/**
 * Dashboard billing API (ADR-0155 Phase D, D1):
 *   GET  /v1/billing/plans            — the purchasable catalog.
 *   POST /v1/billing/checkout-session — start a hosted Stripe Checkout.
 */

import { ApiError, type HttpClient } from './http-client.js';

export interface BillingPlanPrice {
  readonly interval: 'monthly' | 'yearly';
  readonly amount_minor: number;
  readonly currency: string;
  readonly purchasable: boolean;
}

export interface BillingPlanLimits {
  readonly sites: number | null;
  readonly transactions_monthly: number | null;
  readonly agent_traffic_monthly: number | null;
  readonly red_team_runs_monthly: number | null;
  /** Reporting-history window in days; null = unlimited (ADR-0180). */
  readonly retention_days: number | null;
}

export interface BillingPlan {
  readonly plan_id: string;
  readonly name: string;
  readonly slug: string;
  readonly description: string | null;
  readonly trial_days: number;
  readonly limits: BillingPlanLimits;
  readonly prices: readonly BillingPlanPrice[];
}

export interface CurrentSubscription {
  readonly plan_id: string;
  readonly plan_name: string;
  readonly status: string;
  readonly interval: string | null;
  readonly current_period_end: string | null;
  readonly canceled_at: string | null;
  readonly amount_minor: number | null;
  readonly currency: string;
}

export type ListPlansResult =
  | {
      readonly kind: 'ok';
      readonly plans: readonly BillingPlan[];
      readonly current: CurrentSubscription | null;
    }
  | { readonly kind: 'error'; readonly message: string };

export type CheckoutResult =
  | { readonly kind: 'ok'; readonly url: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_purchasable' }
  | { readonly kind: 'rate_limited' }
  | { readonly kind: 'error'; readonly message: string };

export async function listBillingPlans(http: HttpClient): Promise<ListPlansResult> {
  try {
    const res = await http.request<{
      plans: readonly BillingPlan[];
      current: CurrentSubscription | null;
    }>({
      method: 'GET',
      path: '/v1/billing/plans',
    });
    return { kind: 'ok', plans: res?.plans ?? [], current: res?.current ?? null };
  } catch {
    return { kind: 'error', message: 'Could not load plans.' };
  }
}

export interface BillingInvoice {
  readonly id: string;
  readonly number: string | null;
  readonly created: string;
  readonly amount_paid: number;
  readonly currency: string;
  readonly status: string;
  readonly hosted_invoice_url: string | null;
  readonly pdf_url: string | null;
}

export type ListInvoicesResult =
  | { readonly kind: 'ok'; readonly invoices: readonly BillingInvoice[]; readonly has_more: boolean }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

export async function listInvoices(
  http: HttpClient,
  starting_after?: string,
): Promise<ListInvoicesResult> {
  try {
    const path =
      starting_after !== undefined && starting_after.length > 0
        ? `/v1/billing/invoices?starting_after=${encodeURIComponent(starting_after)}`
        : '/v1/billing/invoices';
    const res = await http.request<{ invoices: readonly BillingInvoice[]; has_more: boolean }>({
      method: 'GET',
      path,
    });
    return { kind: 'ok', invoices: res?.invoices ?? [], has_more: res?.has_more === true };
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) return { kind: 'forbidden' };
    return { kind: 'error', message: 'Could not load invoices.' };
  }
}

export type DowngradeResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'no_subscription' }
  | { readonly kind: 'rate_limited' }
  | { readonly kind: 'error'; readonly message: string };

/** Downgrade to the free tier — cancel at period end (no refund). */
export async function downgradeToFree(http: HttpClient): Promise<DowngradeResult> {
  try {
    await http.request<{ ok: boolean }>({ method: 'POST', path: '/v1/billing/downgrade', body: {} });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403) return { kind: 'forbidden' };
      if (err.status === 409) return { kind: 'no_subscription' };
      if (err.status === 429) return { kind: 'rate_limited' };
    }
    return { kind: 'error', message: 'Could not downgrade.' };
  }
}

export async function startCheckout(
  http: HttpClient,
  plan_id: string,
  interval: 'monthly' | 'yearly',
): Promise<CheckoutResult> {
  try {
    const res = await http.request<{ url: string }>({
      method: 'POST',
      path: '/v1/billing/checkout-session',
      body: { plan_id, interval },
    });
    if (res?.url === undefined || res.url.length === 0) {
      return { kind: 'error', message: 'No checkout URL returned.' };
    }
    return { kind: 'ok', url: res.url };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403) return { kind: 'forbidden' };
      if (err.status === 409) return { kind: 'not_purchasable' };
      if (err.status === 429) return { kind: 'rate_limited' };
    }
    return { kind: 'error', message: 'Could not start checkout.' };
  }
}
